import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

export default function DeleteAccountDialog() {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = password.length > 0 && confirm.trim() === "DELETE";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await deleteAccount(password, confirm.trim());
      toast.success("Your account has been deleted.");
      setOpen(false);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete account");
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm text-[#8C3A3A]" data-testid="delete-account-trigger">
          Delete my account
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2 text-[#8C3A3A]">
            <AlertTriangle className="w-5 h-5" /> Delete your account
          </DialogTitle>
          <DialogDescription>
            This permanently deletes your account, your albums, and every photo of you.
            Photos you posted of <em>other</em> people stay with them. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-1">Your password</label>
            <input
              type="password"
              className="mp-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-account-password"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-1">
              Type <span className="text-[#8C3A3A]">DELETE</span> to confirm
            </label>
            <input
              className="mp-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="delete-account-confirm"
              placeholder="DELETE"
              required
            />
          </div>
          <button
            type="submit"
            className="btn-primary w-full !bg-[#8C3A3A]"
            disabled={busy || !canSubmit}
            data-testid="delete-account-submit"
          >
            {busy ? "Deleting…" : "Permanently delete my account"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
